import { useContext } from 'react';

import {
  ListSubheader,
  alpha,
  Box,
  List,
  styled,
  Button,
  ListItem
} from '@mui/material';
import { NavLink as RouterLink } from 'react-router-dom';
import { SidebarContext } from 'src/contexts/SidebarContext';
import iconSvg from './iconMenu';
import { useAppSelector } from 'src/store/hook';

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: #B0B0B0;
      padding: ${theme.spacing(0, 2.5)};
      line-height: 1.4;
    }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {

      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }
    
        .MuiButton-root {
          display: flex;
          color: #6D6D6D;
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};
          border-radius: 12px;

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: #B0B0B0;
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }
          
          .MuiButton-endIcon {
            color: #B0B0B0;
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: #ab6d3400;
            color: #AB6D34;

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: #AB6D34;
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: #AB6D34;
                opacity: 0;
                transition: ${theme.transitions.create([
                  'transform',
                  'opacity'
                ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

function SidebarMenu() {
  const { closeSidebar } = useContext(SidebarContext);
  const userInfo: any = useAppSelector((state) => state.user.user);

  return (
    <>
      <MenuWrapper>
        {mainMenus.map((main, index) => {
          if (main.roles.includes(userInfo?.role)) {
            return (
              <List
                key={index.toString()}
                component="div"
                subheader={
                  <ListSubheader component="div" disableSticky>
                    {main.label}
                  </ListSubheader>
                }
              >
                <SubMenuWrapper>
                  <List component="div">
                    {main.menus.map((menu, i) => {
                      if (menu.roles.includes(userInfo?.role)) {
                        return (
                          <ListItem component="div" key={i.toString()}>
                            <Button
                              disableRipple
                              component={RouterLink}
                              onClick={closeSidebar}
                              to={menu.route}
                              startIcon={menu.icon}
                            >
                              {menu.label}
                            </Button>
                          </ListItem>
                        );
                      }
                    })}
                  </List>
                </SubMenuWrapper>
              </List>
            );
          }
        })}
      </MenuWrapper>
    </>
  );
}

export default SidebarMenu;

const mainMenus = [
  {
    label: '',
    roles: ['HEADQUARTER', 'AREA'],
    menus: [
      {
        label: 'Dashboard',
        route: '/dashboard',
        icon: iconSvg.dashboard,
        roles: ['HEADQUARTER', 'AREA']
      }
    ]
  },
  {
    label: 'Manajemen Anggota',
    roles: ['HEADQUARTER', 'AREA'],
    menus: [
      {
        label: 'Daftar Member',
        route: '/member-list',
        icon: iconSvg.listMember,
        roles: ['HEADQUARTER']
      },
      {
        label: 'Poin Member',
        route: '/point',
        icon: iconSvg.pointManagement,
        roles: ['HEADQUARTER']
      },
      {
        label: 'Voucher Member',
        route: '/member-vouchers',
        icon: iconSvg.memberVoucher,
        roles: ['HEADQUARTER']
      },
      {
        label: 'Pusat Keluhan',
        route: '/complaint-center',
        icon: iconSvg.complaint,
        roles: ['HEADQUARTER', 'AREA']
      }
    ]
  },
  {
    label: 'Manajemen Mitra',
    roles: ['HEADQUARTER', 'AREA'],
    menus: [
      {
        label: 'Master Region',
        route: '/region',
        icon: iconSvg.regionMaster,
        roles: ['HEADQUARTER']
      },
      {
        label: 'Daftar Area',
        route: '/area-list',
        icon: iconSvg.listArea,
        roles: ['HEADQUARTER']
      },
      {
        label: 'Daftar Outlet',
        route: '/outlet-list',
        icon: iconSvg.listOutlet,
        roles: ['HEADQUARTER', 'AREA']
      }
    ]
  },
  {
    label: 'Manajemen Katalog',
    roles: ['HEADQUARTER', 'AREA'],
    menus: [
      {
        label: 'Master Kategori',
        route: '/category-master',
        icon: iconSvg.masterCategory,
        roles: ['HEADQUARTER', 'AREA']
      },
      {
        label: 'Master Produk',
        route: '/product-list',
        icon: iconSvg.listProduct,
        roles: ['HEADQUARTER', 'AREA']
      },
      {
        label: 'Daftar Produk Etalase',
        route: '/storefront-product-list',
        icon: iconSvg.storefrontProductList,
        roles: ['HEADQUARTER', 'AREA']
      }
    ]
  },
  {
    label: 'Transaksi',
    roles: ['HEADQUARTER', 'AREA'],
    menus: [
      {
        label: 'Riwayat Pembayaran',
        route: '/payment-history',
        icon: iconSvg.historyPayment,
        roles: ['HEADQUARTER', 'AREA']
      },
      {
        label: 'Daftar Pesanan',
        route: '/order-list',
        icon: iconSvg.orderList,
        roles: ['HEADQUARTER', 'AREA']
      },
      {
        label: 'Riwayat Penukaran Voucher',
        route: '/voucher-exchange-history',
        icon: iconSvg.exchangeVoucher,
        roles: ['HEADQUARTER', 'AREA']
      }
    ]
  },
  {
    label: 'Manajemen Konten',
    roles: ['HEADQUARTER', 'AREA'],
    menus: [
      {
        label: 'Berita & Promo',
        route: '/news-promotion',
        icon: iconSvg.promo,
        roles: ['HEADQUARTER', 'AREA']
      }
    ]
  },
  {
    label: 'Konfigurasi',
    roles: ['HEADQUARTER', 'AREA'],
    menus: [
      {
        label: 'Aturan Level Badge',
        route: '/badge-level-rule-configuration',
        icon: iconSvg.badge,
        roles: ['HEADQUARTER', 'AREA']
      },
      {
        label: 'Manajemen Voucher',
        route: '/voucher-management-configuration',
        icon: iconSvg.voucherManagement,
        roles: ['HEADQUARTER', 'AREA']
      },
      {
        label: 'Poin Payment',
        route: '/payment-point-configuration',
        icon: iconSvg.pointPayment,
        roles: ['HEADQUARTER', 'AREA']
      },
      {
        label: 'Pengiriman (soon)',
        route: '/delivery',
        icon: iconSvg.delivery,
        roles: ['HEADQUARTER', 'AREA']
      },
      {
        label: 'Kategori Keluhan',
        route: '/complaint-category-configuration',
        icon: iconSvg.complaintCategory,
        roles: ['HEADQUARTER']
      },
      {
        label: 'Manajemen Admin',
        route: '/admin-management-configuration',
        icon: iconSvg.adminManagement,
        roles: ['HEADQUARTER']
      }
    ]
  }
];
